import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import { CustomerResponse } from '../types';
import { customerApi, applicationsApi, aopApi } from '../services/api';
import { getAllProjects } from '../services/project-service';
import { mapDashboardResponseToCustomerResponse } from '../utils/dashboard-api-mapper';

/** Section keys used for lazy loading; only these are fetched on tab switch */
export type CustomerSection = 'goals' | 'technology' | 'applications' | 'aop' | 'frameworks' | 'projects' | 'maturity';

interface CustomerContextType {
    customerData: CustomerResponse | undefined;
    isLoading: boolean;
    error: string | null;
    currentCustomerId: string | null;
    setCustomerData: (data: CustomerResponse | undefined) => void;
    fetchCustomerData: (customerId: string) => Promise<void>;
    fetchDashboard: (customerId: string) => Promise<void>;
    isDashboardLoaded: () => boolean;
    fetchSection: (customerId: string, section: CustomerSection, force?: boolean) => Promise<void>;
    isSectionLoaded: (section: CustomerSection) => boolean;
    loadingSection: CustomerSection | null;
    dashboardLoading: boolean;
    updateCustomerData: (updates: Partial<CustomerResponse>) => void;
    clearCustomerData: () => void;
    refreshCustomerData: () => Promise<void>;
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

export const useCustomer = () => {
    const context = useContext(CustomerContext);
    if (context === undefined) {
        throw new Error('useCustomer must be used within a CustomerProvider');
    }
    return context;
};

/** Empty section defaults so CustomerResponse shape is satisfied when only details are loaded */
function emptyCustomerSections(industryType: string = ''): Partial<CustomerResponse> {
    return {
        art_of_possible: { aops: [] },
        critical_apps: { critical_apps_list: [], industry_type: industryType },
        customer_contact_details: { contact_details: [] },
        frameworks: { frameworks: [] },
        industry_details: { industry_goals_and_output: [], industry_name: '', industry_trend_details: [], industry_priority_details: [] },
        maturity_levels: { maturity_levels: [], industry_type: industryType },
        notes: { notes: [] },
        technology: { key_technology: [], strategic_vendors: [] },
        top_10_projects: { top_projects: [], industry_type: industryType },
    };
}

/** Map project-service response (projects array) to TopProjectsResponse for context */
function mapProjectsToTop10(response: { projects?: any[] }, industryType: string = '') {
    const raw = response?.projects ?? [];
    const top_projects = raw.map((p: any) => ({
        project_id: p.projectId ?? p.project_id ?? '',
        name: p.projectName ?? p.name ?? '',
        overview_and_goals: p.overview ?? p.overview_and_goals ?? '',
        presidio_controlled: p.presidioControlled ?? p.presidio_controlled,
        modernization_plan_project: p.modernizationPlanProject ?? p.modernization_plan_project,
        start_month: p.startDate ?? p.start_month,
        end_month: p.endDate ?? p.end_month,
        dependency_project_id: p.dependsOnProjectId ?? p.dependency_project_id,
        goals: p.goals ?? [],
        presidio_areas: p.presidioAreas ?? p.presidio_areas ?? [],
    }));
    return { top_projects, industry_type: industryType };
}

interface CustomerProviderProps {
    children: ReactNode;
}

export const CustomerProvider: React.FC<CustomerProviderProps> = ({ children }) => {
    const [customerData, setCustomerDataState] = useState<CustomerResponse | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [currentCustomerId, setCurrentCustomerId] = useState<string | null>(null);
    const [loadingSection, setLoadingSection] = useState<CustomerSection | null>(null);
    const [dashboardLoading, setDashboardLoading] = useState<boolean>(false);
    const loadedSectionsRef = useRef<Record<string, boolean>>({});

    const clearLoadedSections = useCallback(() => {
        loadedSectionsRef.current = {};
    }, []);

    const dashboardLoadedCustomerIdRef = useRef<string | null>(null);

    const setCustomerData = useCallback((data: CustomerResponse | undefined) => {
        if (data?.customer_id && data.customer_id !== currentCustomerId) {
            clearLoadedSections();
            dashboardLoadedCustomerIdRef.current = null;
        }
        if (!data) {
            clearLoadedSections();
            dashboardLoadedCustomerIdRef.current = null;
        }
        setCustomerDataState(data);
        if (data?.customer_id) {
            setCurrentCustomerId(data.customer_id);
        }
        setError(null);
    }, [currentCustomerId, clearLoadedSections]);

    const isSectionLoaded = useCallback((section: CustomerSection) => {
        return !!loadedSectionsRef.current[section];
    }, []);

    /** Fetches only customer details (no goals, technology, applications, AOP, frameworks). */
    const fetchCustomerData = useCallback(async (customerId: string) => {
        if (currentCustomerId && currentCustomerId !== customerId) {
            setCustomerDataState(undefined);
            clearLoadedSections();
            dashboardLoadedCustomerIdRef.current = null;
        }

        setIsLoading(true);
        setError(null);
        setCurrentCustomerId(customerId);

        try {
            const customerDetails = await customerApi.getCustomerDetails(customerId);
            if (!customerDetails?.customer_details) {
                throw new Error('Customer profile was not found or is unavailable.');
            }
            const industryType = (customerDetails as any)?.customer_details?.industry_type ?? '';
            const data: CustomerResponse = {
                ...emptyCustomerSections(industryType),
                ...customerDetails,
                customer_id: customerDetails.customer_id ?? customerId,
            } as CustomerResponse;
            setCustomerDataState(data);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch customer data';
            setError(errorMessage);
            console.error('Error fetching customer data:', err);
        } finally {
            setIsLoading(false);
        }
    }, [currentCustomerId, clearLoadedSections]);

    const isDashboardLoaded = useCallback(() => {
        return dashboardLoadedCustomerIdRef.current === currentCustomerId;
    }, [currentCustomerId]);

    const fetchDashboard = useCallback(async (customerId: string) => {
        if (currentCustomerId && currentCustomerId !== customerId) return;

        setDashboardLoading(true);
        setError(null);
        try {
            const dashboard = await customerApi.getDashboard(customerId);
            const mapped = mapDashboardResponseToCustomerResponse(dashboard);
            setCustomerDataState(prev => {
                const base = prev ?? ({} as CustomerResponse);
                return { ...base, ...mapped } as CustomerResponse;
            });
            dashboardLoadedCustomerIdRef.current = customerId;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch dashboard';
            setError(errorMessage);
            console.error('Error fetching dashboard:', err);
        } finally {
            setDashboardLoading(false);
        }
    }, [currentCustomerId]);

    const fetchSection = useCallback(async (customerId: string, section: CustomerSection, force: boolean = false) => {
        if (currentCustomerId && currentCustomerId !== customerId) return;
        if (!force && loadedSectionsRef.current[section]) return;

        setLoadingSection(section);
        try {
            const industryType = customerData?.customer_details?.industry_type ?? '';

            switch (section) {
                case 'goals': {
                    const goals = await customerApi.getAllGoals(customerId);
                    setCustomerDataState(prev => prev ? { ...prev, goals } : prev);
                    break;
                }
                case 'technology': {
                    const technology = await customerApi.getCustomerTechnology(customerId);
                    if (technology) {
                        setCustomerDataState(prev => prev ? { ...prev, technology } : prev);
                    }
                    break;
                }
                case 'applications': {
                    const applicationsList = await applicationsApi.getApplications(customerId);
                    setCustomerDataState(prev => prev ? {
                        ...prev,
                        critical_apps: { critical_apps_list: applicationsList, industry_type: prev.customer_details?.industry_type ?? '' },
                    } : prev);
                    break;
                }
                case 'aop': {
                    const aopResponse = await aopApi.getCustomerAop(customerId);
                    const artOfPossible = {
                        aops: (aopResponse.aop_items || []).map((item: any) => ({
                            area: item.label,
                            notes: (item.plans || []).map((plan: any) => ({
                                name: plan.name ?? '',
                                rank: plan.rank ?? 0,
                                growth: plan.technical_impact ?? '',
                                priority: plan.business_impact ?? '',
                            })),
                        })),
                    };
                    setCustomerDataState(prev => prev ? { ...prev, art_of_possible: artOfPossible } : prev);
                    break;
                }
                case 'frameworks': {
                    const frameworkResponse = await customerApi.getCustomerFramework(customerId);
                    setCustomerDataState(prev => prev ? { ...prev, frameworks: { frameworks: frameworkResponse.frameworks } } : prev);
                    break;
                }
                case 'projects': {
                    const response = await getAllProjects(customerId);
                    const top_10_projects = mapProjectsToTop10(
                        Array.isArray(response) ? { projects: [] } : response,
                        industryType
                    );
                    setCustomerDataState(prev => prev ? { ...prev, top_10_projects } : prev);
                    break;
                }
                case 'maturity':
                    // Maturity is fetched by dashboard components from API on dropdown selection; not stored in context
                    break;
                default:
                    break;
            }
            loadedSectionsRef.current[section] = true;
        } catch (err) {
            console.warn(`Section ${section} fetch failed:`, err);
        } finally {
            setLoadingSection(null);
        }
    }, [currentCustomerId, customerData?.customer_details?.industry_type]);

    const updateCustomerData = useCallback((updates: Partial<CustomerResponse>) => {
        setCustomerDataState(prev => {
            if (!prev) return prev;
            return { ...prev, ...updates };
        });
    }, []);

    const clearCustomerData = useCallback(() => {
        setCustomerDataState(undefined);
        setCurrentCustomerId(null);
        setError(null);
        clearLoadedSections();
        dashboardLoadedCustomerIdRef.current = null;
    }, [clearLoadedSections]);

    const refreshCustomerData = useCallback(async () => {
        if (currentCustomerId) {
            await fetchCustomerData(currentCustomerId);
        }
    }, [currentCustomerId, fetchCustomerData]);

    const value = {
        customerData,
        isLoading,
        error,
        currentCustomerId,
        setCustomerData,
        fetchCustomerData,
        fetchDashboard,
        isDashboardLoaded,
        fetchSection,
        isSectionLoaded,
        loadingSection,
        dashboardLoading,
        updateCustomerData,
        clearCustomerData,
        refreshCustomerData,
    };

    return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
};
